import axios from 'axios';

interface WPMediaItem {
  id: number;
  source_url: string;
  title: { rendered: string };
  media_details: {
    sizes?: {
      thumbnail?: { source_url: string };
      medium?: { source_url: string };
      large?: { source_url: string };
    };
    filesize?: number;
    length_formatted?: string;
    mime_type?: string;
  };
  date: string;
  mime_type: string;
}

interface WPMediaQueryParams {
  page?: number;
  per_page?: number;
  search?: string;
  media_type?: string;
  mime_type?: string;
  orderby?: 'date' | 'title';
  order?: 'asc' | 'desc';
}

export class WordPressMediaService {
  private static instance: WordPressMediaService;
  private token: string | null = null;
  private baseUrl = 'https://wp.sinopseaeaav.pt';
  private uploadsUrl = 'https://wp.sinopseaeaav.pt/wp-content/uploads';
  private username = 'sinopsea';
  private applicationPassword = 'BCmD TD1K l30O iQIa AMJb FgGn';

  private constructor() {
    console.log('Inicializando WordPressMediaService...');
    this.initializeAuth();
  }

  static getInstance(): WordPressMediaService {
    if (!WordPressMediaService.instance) {
      console.log('Criando nova instância do WordPressMediaService');
      WordPressMediaService.instance = new WordPressMediaService();
    }
    return WordPressMediaService.instance;
  }

  private async initializeAuth() {
    try {
      console.log('Iniciando autenticação WordPress...');
      const authString = btoa(`${this.username}:${this.applicationPassword}`);
      this.token = `Basic ${authString}`;
      
      // Teste a autenticação com uma chamada simples
      const testResponse = await axios.get(`${this.baseUrl}/wp-json/wp/v2/media`, {
        headers: {
          'Accept': 'application/json',
          'Authorization': this.token
        },
        params: { per_page: 1 }
      });
      
      if (testResponse.status === 200) {
        console.log('Autenticação WordPress testada e confirmada com sucesso');
      } else {
        throw new Error(`Teste de autenticação falhou: ${testResponse.status}`);
      }
    } catch (error) {
      console.error('Erro na autenticação WordPress:', error);
      this.token = null;
      throw new Error('Falha na autenticação WordPress');
    }
  }

  private getHeaders() {
    const headers: Record<string, string> = {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    };

    if (this.token) {
      headers['Authorization'] = this.token;
    } else {
      console.warn('No authentication token available');
    }

    return headers;
  }

  private sanitizeUrl(url: string): string {
    if (!url) return '';
    
    try {
      if (!url.startsWith('http')) {
        url = `${this.uploadsUrl}/${url}`;
      }
      
      url = url.replace(/([^:]\/)\/+/g, '$1');
      
      if (url.includes('/wp-content/uploads')) {
        url = url.replace(this.baseUrl, this.uploadsUrl);
        url = url.replace(/\/wp-content\/uploads\/.*?(wp-content\/uploads)/g, '/wp-content/uploads');
      }
      
      console.log('Sanitized URL:', {
        original: url,
        sanitized: url
      });
      
      return url;
    } catch (error) {
      console.error('Error sanitizing URL:', error);
      return url;
    }
  }

  private createCloneableObject<T extends object>(obj: T): T {
    try {
      return JSON.parse(JSON.stringify(obj));
    } catch (error) {
      console.error('Error creating cloneable object:', error);
      return {} as T;
    }
  }

  private sanitizeMediaItem(item: any): WPMediaItem {
    try {
      const sourceUrl = this.sanitizeUrl(item.source_url || '');
      console.log('Processing media item:', {
        id: item.id,
        originalUrl: item.source_url,
        sanitizedUrl: sourceUrl
      });

      const sanitizedItem = {
        id: item.id || 0,
        source_url: sourceUrl,
        title: {
          rendered: item.title?.rendered || ''
        },
        media_details: {
          sizes: item.media_details?.sizes || {},
          filesize: item.media_details?.filesize || 0,
          length_formatted: item.media_details?.length_formatted || '',
          mime_type: item.media_details?.mime_type || ''
        },
        date: item.date || new Date().toISOString(),
        mime_type: item.mime_type || 'image/jpeg'
      };

      if (sanitizedItem.media_details.sizes) {
        Object.keys(sanitizedItem.media_details.sizes).forEach(size => {
          const sizeObj = sanitizedItem.media_details.sizes![size];
          if (sizeObj && sizeObj.source_url) {
            sizeObj.source_url = this.sanitizeUrl(sizeObj.source_url);
          }
        });
      }

      return this.createCloneableObject(sanitizedItem);
    } catch (error) {
      console.error('Error sanitizing media item:', error);
      throw error;
    }
  }

  async listMedia(params: WPMediaQueryParams = {}): Promise<{
    items: WPMediaItem[];
    total: number;
    totalPages: number;
  }> {
    try {
      if (!this.token) {
        await this.initializeAuth();
      }

      console.log('Requesting WordPress media...');
      
      const response = await axios.get(`${this.baseUrl}/wp-json/wp/v2/media`, {
        params: {
          ...params,
          per_page: params.per_page || 100,
          orderby: params.orderby || 'date',
          order: params.order || 'desc'
        },
        headers: this.getHeaders(),
        timeout: 10000,
        withCredentials: false
      });

      console.log('WordPress response:', response);

      if (!response.data) {
        throw new Error('No data received from WordPress');
      }

      const items = Array.isArray(response.data) ? response.data.map(item => this.sanitizeMediaItem(item)) : [];
      const total = parseInt(response.headers['x-wp-total'] || '0');
      const totalPages = parseInt(response.headers['x-wp-totalpages'] || '1');

      return {
        items,
        total,
        totalPages
      };
    } catch (error) {
      console.error('Error fetching WordPress media:', error);
      throw error;
    }
  }

  async uploadMedia(file: File | Blob): Promise<WPMediaItem> {
    if (!this.token) {
      throw new Error('Authentication required for upload');
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${this.baseUrl}/wp-json/wp/v2/media`, formData, {
        headers: {
          ...this.getHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      });

      return this.sanitizeMediaItem(response.data);
    } catch (error) {
      console.error('Error uploading media:', error);
      throw new Error('Failed to upload media');
    }
  }

  async deleteMedia(id: number): Promise<void> {
    if (!this.token) {
      throw new Error('Authentication required for deletion');
    }

    try {
      await axios.delete(`${this.baseUrl}/wp-json/wp/v2/media/${id}?force=true`, {
        headers: this.getHeaders(),
      });
    } catch (error) {
      console.error('Error deleting media:', error);
      throw new Error('Failed to delete media');
    }
  }
}

export const wpMedia = WordPressMediaService.getInstance();
